// frontend/src/pages/Socrates/components/TemplateManagement/SystemTemplateList/index.js

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlobe, faEye, faEdit, faTrash,
  faCalendarAlt, faCheckCircle, faTimesCircle, faCog,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useTemplate } from '../../../contexts/TemplateContext';
import { useSocrates } from '../../../contexts/SocratesContext';
import SocratesCard from '../../common/SocratesCard';
import styles from './SystemTemplateList.module.css';

const SystemTemplateList = ({ templates, onTemplateSelect }) => {
  const { t } = useLanguage();
  const { setSelectedTemplate, openEditForm, deleteTemplate } = useTemplate();
  const { isSuperAdmin } = useSocrates();

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    onTemplateSelect(template);
  };

  const handleEditClick = (e, template) => {
    e.stopPropagation();
    if (isSuperAdmin) {
      openEditForm(template);
    }
  };

  const handleDeleteClick = (e, template) => {
    e.stopPropagation();
    if (isSuperAdmin && window.confirm(t('confirm_delete_template', { name: template.template_name }))) {
      deleteTemplate(template.template_id);
    }
  };

  if (!templates || templates.length === 0) {
    return (
      <div className={styles.emptyState}>
        <FontAwesomeIcon icon={faGlobe} className={styles.emptyIcon} />
        <h3 className={styles.emptyTitle}>{t('no_system_templates')}</h3>
        <p className={styles.emptyDescription}>
          {t('system_templates_will_appear_here')}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.systemTemplateList}>
      <div className={styles.listHeader}>
        <h2 className={styles.listTitle}>
          <FontAwesomeIcon icon={faGlobe} className={styles.titleIcon} />
          {t('system_templates')}
        </h2>
        <p className={styles.listDescription}>
          {t('system_templates_description')}
        </p>
      </div>

      <div className={styles.templateGrid}>
        {templates.map((template) => (
          <SocratesCard
            key={template.template_id}
            className={styles.templateCard}
            onClick={() => handleTemplateClick(template)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon icon={faGlobe} className={styles.cardIcon} />
                <h3 className={styles.templateName}>{template.template_name}</h3>
              </div>
              <div className={styles.cardStatus}>
                {template.is_active === 'true' ? (
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.activeIcon} />
                ) : (
                  <FontAwesomeIcon icon={faTimesCircle} className={styles.inactiveIcon} />
                )}
              </div>
            </div>

            <div className={styles.cardContent}>
              <p className={styles.templateDescription}>
                {template.template_description || t('no_description_available')}
              </p>
              
              <div className={styles.templateMeta}>
                <div className={styles.metaItem}>
                  <FontAwesomeIcon icon={faCog} className={styles.metaIcon} />
                  <span className={styles.metaLabel}>{t('parameters')}:</span>
                  <span className={styles.metaValue}>
                    {template.parameters?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.cardFooter}>
              <div className={styles.actionButtons}>
                <button 
                  className={styles.viewButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateClick(template);
                  }}
                  title={t('view_details')}
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>{t('view')}</span>
                </button>
                
                <button 
                  className={`${styles.editButton} ${!isSuperAdmin ? styles.disabled : ''}`}
                  onClick={(e) => handleEditClick(e, template)}
                  title={!isSuperAdmin ? t('requires_super_admin_role') : t('edit_template')}
                  disabled={!isSuperAdmin}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  <span>{t('edit')}</span>
                </button>
                
                <button 
                  className={`${styles.deleteButton} ${!isSuperAdmin ? styles.disabled : ''}`}
                  onClick={(e) => handleDeleteClick(e, template)}
                  title={!isSuperAdmin ? t('requires_super_admin_role') : t('delete_template')}
                  disabled={!isSuperAdmin}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <span>{t('delete')}</span>
                </button>
              </div>
              
              {!isSuperAdmin && (
                <div className={styles.adminOnlyBadge} title={t('requires_super_admin_role')}>
                  <FontAwesomeIcon icon={faLock} />
                  <span>{t('admin_only')}</span>
                </div>
              )}
              
              <div className={styles.templateId}>
                <span className={styles.idLabel}>{t('id')}:</span>
                <span className={styles.idValue}>
                  {template.template_id.substring(0, 8)}...
                </span>
              </div>
            </div>
          </SocratesCard>
        ))}
      </div>
    </div>
  );
};

export default SystemTemplateList;